// StudentAttendanceView.js
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import { getFacultyAssignedClassNames, filterClassesForFaculty } from '../../utils/facultyHierarchyUtils';
import { fetchAttendanceRecords, fetchAllAttendanceRecords } from '../../services/attendanceService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './StudentAttendanceView.css';
// Material Icons
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SendIcon from '@mui/icons-material/Send';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

const mapStatusFromBackend = (status) => {
  switch (status) {
    case 'PRESENT': return 'Present';
    case 'ABSENT': return 'Absent';
    case 'LATE': return 'Late';
    case 'ON_LEAVE': return 'Leave';
    default: return status || 'Present';
  }
};

const formatLocalDate = (dateVal) => {
  if (!dateVal) return '';
  const pad = (num) => num < 10 ? `0${num}` : `${num}`;
  if (Array.isArray(dateVal)) {
    const [year, month, day] = dateVal;
    return `${year}-${pad(month)}-${pad(day)}`;
  }
  if (typeof dateVal === 'object') {
    const year = dateVal.year;
    const month = dateVal.monthValue || dateVal.month;
    const day = dateVal.dayOfMonth || dateVal.day;
    if (year && month && day) {
      let m = month;
      if (typeof month === 'string') {
        const months = {
          JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6,
          JULY: 7, AUGUST: 8, SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12
        };
        m = months[month.toUpperCase()] || 1;
      }
      return `${year}-${pad(m)}-${pad(day)}`;
    }
  }
  if (typeof dateVal === 'string') {
    return dateVal.split('T')[0];
  }
  return dateVal.toString();
};

const StudentAttendanceView = () => {
  const { user, studentInfo, userRole, axios, token, orgId } = useAuth();

  // If in student role, use their real ID. 
  // In demo/admin role or if student ID is not found, we can select from the seed database.
  const selectedStudentId = studentInfo?.userId || user?.userId || '';
  const selectedClassId = studentInfo?.classId || user?.classId || '';

  const [classesList, setClassesList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [apiStudents, setApiStudents] = useState([]);
  const [isLoadingApi, setIsLoadingApi] = useState(false);

  // Calendar State
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1); 
  });
  
  // Selected date for details panel
  const [selectedDateStr, setSelectedDateStr] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Load Classes, Subjects, and Students dynamically from API
  useEffect(() => {
    const fetchApiData = async () => {
      if (!orgId || !token) return;
      setIsLoadingApi(true);
      try {
        // 1. Fetch Classes
        let classes = [];
        try {
          const res = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
          const rawClasses = res.data?.items || res.data || [];
          classes = rawClasses.map(c => {
            const centerName = isCustomOrg
              ? (c.className || c.label || c.name || '').trim()
              : (c.name || (c.sectionName && c.sectionName !== 'null' && c.sectionName !== 'undefined' ? `${c.className || ''}-${c.sectionName}` : c.className || '')).trim();
            return {
              classId: c.classId || c.id,
              value: c.classId || c.id,
              className: c.className || centerName,
              sectionName: isCustomOrg ? '' : (c.sectionName || ''),
              name: centerName || 'Unknown Center',
              label: centerName || 'Unknown Center'
            };
          });

          const assignedClassNames = await getFacultyAssignedClassNames(orgId, user, axios);
          classes = filterClassesForFaculty(classes, assignedClassNames);
        } catch (e) {
          console.warn("Failed API classes fetch in Student view", e);
        }
        if (!classes) classes = [];
        setClassesList(classes);

        // 2. Fetch Subjects
        let subjects = [];
        try {
          const res = await axios.get(`${configData.GET_SUBJECTS_BY_ORG}/${orgId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const rawSubjects = res.data?.items || res.data || [];
          subjects = rawSubjects.map(sub => ({
            courseCode: sub.courseCode || sub.id,
            courseName: sub.courseDetails?.courseName || sub.name || 'Unknown Subject',
            period: sub.period || 'General'
          }));
        } catch (e) {
          console.warn("Failed API subjects fetch in Student view", e);
        }
        if (!subjects) subjects = [];
        setSubjectsList(subjects);

        // 3. Fetch Students for all classes (to build demo switcher list)
        const allFetchedStudents = [];
        for (const cls of classes) {
          try {
            const res = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${cls.classId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const rawStudents = res.data?.items || res.data || [];
            const studentsInClass = rawStudents.map(item => {
              if (item.id && item.name && item.admissionNumber) {
                return item;
              }
              const userObj = item.user || {};
              const studentInfoObj = item.studentInfo || {};
              return {
                id: userObj.userId || item.id || '',
                name: `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || item.name || 'Unknown Student',
                admissionNumber: studentInfoObj.admissionId || item.admissionNumber || 'N/A',
                active: userObj.active !== false && userObj.status !== 'Inactive',
                status: userObj.status || 'Active',
                remark: item.remark || ''
              };
            });
            const activeStudents = studentsInClass.filter(s => s.active && s.status !== 'Inactive');
            


            studentsInClass.forEach(s => {
              if (s.active && s.status !== 'Inactive') {
                allFetchedStudents.push({
                  id: s.id,
                  name: s.name,
                  admissionNumber: s.admissionNumber,
                  classId: cls.classId,
                  className: cls.name || `${cls.className}-${cls.sectionName}`
                });
              }
            });
          } catch (e) {
            console.warn(`Failed API student fetch in Student view for class ${cls.classId}`, e);
          }
        }
        
        if (allFetchedStudents.length > 0) {
          setApiStudents(allFetchedStudents);
        }
      } catch (err) {
        console.error("Error fetching API data for student view", err);
      } finally {
        setIsLoadingApi(false);
      }
    };

    fetchApiData();
  }, [orgId, token, axios]);

  // Find currently selected student details
  const activeStudent = useMemo(() => {
    const classObj = classesList.find(c => c.classId === selectedClassId);
    return {
      id: selectedStudentId,
      name: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.name || 'Logged In Student',
      admissionNumber: studentInfo?.admissionId || 'N/A',
      classId: selectedClassId,
      className: classObj?.name || 'Class Details'
    };
  }, [selectedStudentId, selectedClassId, user, studentInfo, classesList]);

  // State to hold backend-loaded student attendance records
  const [backendRecords, setBackendRecords] = useState([]);
  const [isRecordsLoading, setIsRecordsLoading] = useState(false);

  // Fetch student attendance records from backend CampusResources API
  useEffect(() => {
    const fetchStudentAttendance = async () => {
      if (!token || !selectedStudentId) return;
      setIsRecordsLoading(true);
      try {
        const result = await fetchAllAttendanceRecords({
          filterType: 'user',
          id: selectedStudentId,
          startDate: `${new Date().getFullYear()}-01-01`,
          endDate: `${new Date().getFullYear()}-12-31`
        });
        const list = result || [];
        const mappedBackend = list.map(r => ({
          date: formatLocalDate(r.attendanceDate),
          subjectCode: r.subject || 'General',
          subjectName: r.subject || 'General',
          period: 'General',
          status: mapStatusFromBackend(r.attendanceStatus),
          remark: r.remarks || ''
        }));

        setBackendRecords(mappedBackend);
      } catch (e) {
        console.warn("Failed to fetch student attendance from backend", e);
        setBackendRecords([]);
      } finally {
        setIsRecordsLoading(false);
      }
    };
    fetchStudentAttendance();
  }, [selectedStudentId, token]);

  // Calculate stats for the selected student
  const stats = useMemo(() => {
    const totalClasses = backendRecords.length;
    let presentCount = 0;
    let absentCount = 0;
    let lateCount = 0;
    let leaveCount = 0;

    backendRecords.forEach(r => {
      if (r.status === 'Present') presentCount++;
      else if (r.status === 'Absent') absentCount++;
      else if (r.status === 'Late') lateCount++;
      else if (r.status === 'Leave') leaveCount++;
    });

    const attendedCount = presentCount + lateCount;
    const percentage = totalClasses > 0 ? Math.round((attendedCount / totalClasses) * 100) : 100;

    return {
      percentage,
      totalClasses,
      presentCount,
      absentCount,
      lateCount,
      leaveCount,
      records: backendRecords
    };
  }, [backendRecords]);



  // Handle month navigation
  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDayIndex = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    
    // Pad previous month days
    const prevMonthTotalDays = new Date(year, month, 0).getDate();
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthTotalDays - i);
      days.push({
        date: d,
        dayNum: d.getDate(),
        isCurrentMonth: false,
        dateStr: d.toISOString().split('T')[0]
      });
    }
    
    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      const d = new Date(year, month, i);
      days.push({
        date: d,
        dayNum: i,
        isCurrentMonth: true,
        dateStr: d.toISOString().split('T')[0]
      });
    }
    
    // Pad next month days to make complete weeks (multiples of 7)
    const totalSlots = Math.ceil(days.length / 7) * 7;
    const nextDaysCount = totalSlots - days.length;
    for (let i = 1; i <= nextDaysCount; i++) {
      const d = new Date(year, month + 1, i);
      days.push({
        date: d,
        dayNum: i,
        isCurrentMonth: false,
        dateStr: d.toISOString().split('T')[0]
      });
    }
    
    // Add attendance data to each day
    const recordsList = Array.isArray(stats?.records) ? stats.records : [];
    return days.map(day => {
      // Filter records for this day
      const dayRecords = recordsList.filter(r => r.date === day.dateStr);
      
      let dominantStatus = 'empty';
      let classesCount = dayRecords.length;
      
      if (classesCount > 0) {
        const hasAbsent = dayRecords.some(r => r.status === 'Absent');
        const hasLate = dayRecords.some(r => r.status === 'Late');
        const hasLeave = dayRecords.some(r => r.status === 'Leave');
        const allPresent = dayRecords.every(r => r.status === 'Present');
        
        if (hasAbsent) dominantStatus = 'Absent';
        else if (hasLate) dominantStatus = 'Late';
        else if (hasLeave) dominantStatus = 'Leave';
        else if (allPresent) dominantStatus = 'Present';
      }
      
      const isWeekend = day.date.getDay() === 0; // Sunday
      
      return {
        ...day,
        records: dayRecords,
        dominantStatus,
        isWeekend
      };
    });
  }, [currentMonth, stats?.records]);

  // Selected date records details
  const selectedDateDetails = useMemo(() => {
    const dayObj = calendarDays.find(d => d.dateStr === selectedDateStr);
    return dayObj || { dateStr: selectedDateStr, records: [], dominantStatus: 'empty', isWeekend: false };
  }, [selectedDateStr, calendarDays]);

  // Defaulter warning check
  const isDefaulter = stats.percentage < 75;

  return (
    <div className="student-view-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header Panel */}
      <div className="student-view-header">
        <div>
          <h1>My Attendance Dashboard</h1>
          <p>Real-time monthly calendars, stats tracker, and instant correction logs.</p>
        </div>
        

      </div>

      {/* Warning Defaulter Banner */}
      {isDefaulter && (
        <div className="defaulter-alert-banner">
          <ErrorOutlineIcon style={{ fontSize: '24px' }} />
          <div>
            <strong>Attendance Defaulter Warning:</strong> Your current attendance of <strong>{stats.percentage}%</strong> is below the school's minimum required threshold of <strong>75%</strong>. Please contact your KV Center supervisor to resolve any pending leaves or submit necessary medical slips.
          </div>
        </div>
      )}

      {/* Info Profile Card */}
      <div className="student-profile-info-strip">
        <div className="profile-badge-icon" style={{ background: stats.percentage >= 75 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
          {activeStudent.name.charAt(0)}
        </div>
        <div className="profile-meta-grid">
          <div>
            <span className="meta-label">STUDENT NAME</span>
            <span className="meta-val">{activeStudent.name}</span>
          </div>
          <div>
            <span className="meta-label">ADMISSION ID</span>
            <span className="meta-val">{activeStudent.admissionNumber}</span>
          </div>
          <div>
            <span className="meta-label">KV CENTER</span>
            <span className="meta-val">{activeStudent.className}</span>
          </div>
          <div>
            <span className="meta-label">DASHBOARD PERMISSION</span>
            <span className="meta-val role-badge">{userRole} view</span>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="dashboard-stats-grid">
        {/* Percentage Gauge Meter */}
        <div className="stats-metric-card circular-gauge-card">
          <h3>Overall Summary</h3>
          <div className="gauge-meter-wrapper">
            <svg viewBox="0 0 100 100" className="gauge-svg">
              {/* Background circle */}
              <circle cx="50" cy="50" r="40" className="gauge-bg" />
              {/* Highlight circle */}
              <circle
                cx="50"
                cy="50"
                r="40"
                className={`gauge-fill ${stats.percentage >= 75 ? 'good' : 'warning'}`}
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - stats.percentage / 100)}`}
                transform="rotate(-90 50 50)"
              />
            </svg>
            <div className="gauge-center-text">
              <span className="gauge-val">{stats.percentage}%</span>
              <span className="gauge-label">Attended</span>
            </div>
          </div>
          <div className="gauge-footer-text">
            {stats.percentage >= 75 ? (
              <span className="status-good-badge">✓ Good Standing</span>
            ) : (
              <span className="status-critical-badge">⚠ Below Attendance Standard</span>
            )}
          </div>
        </div>

        {/* Breakdown Numeric Cards */}
        <div className="breakdown-cards-grid">
          <div className="metric-num-card sessions">
            <span className="num-icon"><CalendarMonthIcon /></span>
            <div>
              <h4>Total Sessions</h4>
              <h2>{stats.totalClasses}</h2>
              <p>Lessons scheduled</p>
            </div>
          </div>

          <div className="metric-num-card present">
            <span className="num-icon green"><CheckCircleIcon /></span>
            <div>
              <h4>Attended Present</h4>
              <h2>{stats.presentCount}</h2>
              <p>Marked fully present</p>
            </div>
          </div>

          <div className="metric-num-card late">
            <span className="num-icon amber"><ScheduleIcon /></span>
            <div>
              <h4>Arrived Late</h4>
              <h2>{stats.lateCount}</h2>
              <p>Excused arrivals</p>
            </div>
          </div>

          <div className="metric-num-card absent">
            <span className="num-icon red"><CancelIcon /></span>
            <div>
              <h4>Absences Recorded</h4>
              <h2>{stats.absentCount}</h2>
              <p>Uninformed absences</p>
            </div>
          </div>

          <div className="metric-num-card leave">
            <span className="num-icon blue"><BeachAccessIcon /></span>
            <div>
              <h4>Excused Leaves</h4>
              <h2>{stats.leaveCount}</h2>
              <p>Approved certificates</p>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Attendance Calendar & Details */}
      <div className="calendar-interaction-section">
        {/* Calendar Card */}
        <div className="interactive-calendar-card">
          <div className="calendar-card-header">
            <h3>Attendance Calendar</h3>
            
            <div className="calendar-nav">
              <button className="nav-arrow-btn" onClick={prevMonth}>
                <ArrowBackIosNewIcon style={{ fontSize: '14px' }} />
              </button>
              <span className="month-name-display">
                {currentMonth.toLocaleDateString('default', { month: 'long', year: 'numeric' })}
              </span>
              <button className="nav-arrow-btn" onClick={nextMonth}>
                <ArrowForwardIosIcon style={{ fontSize: '14px' }} />
              </button>
            </div>
          </div>

          <div className="calendar-legend-strip">
            <div className="legend-item"><span className="legend-color-dot present"></span> Present</div>
            <div className="legend-item"><span className="legend-color-dot late"></span> Late</div>
            <div className="legend-item"><span className="legend-color-dot absent"></span> Absent</div>
            <div className="legend-item"><span className="legend-color-dot leave"></span> Leave</div>
            <div className="legend-item"><span className="legend-color-dot holiday"></span> Sunday/Closed</div>
          </div>

          {/* Calendar Grid */}
          <div className="calendar-grid-wrapper">
            <div className="calendar-grid-header">
              <div>SUN</div>
              <div>MON</div>
              <div>TUE</div>
              <div>WED</div>
              <div>THU</div>
              <div>FRI</div>
              <div>SAT</div>
            </div>
            <div className="calendar-grid-cells">
              {calendarDays.map((day, idx) => {
                const isSelected = day.dateStr === selectedDateStr;
                const hasClasses = day.records.length > 0;
                
                return (
                  <div
                    key={idx}
                    className={`calendar-cell ${day.isCurrentMonth ? 'current-month' : 'other-month'} ${day.dominantStatus} ${isSelected ? 'selected' : ''} ${day.isWeekend ? 'weekend' : ''}`}
                    onClick={() => setSelectedDateStr(day.dateStr)}
                  >
                    <span className="cell-day-number">{day.dayNum}</span>
                    
                    {hasClasses && (
                      <div className="cell-indicators">
                        {(day.records || []).map((r, rIdx) => (
                          <span key={rIdx} className={`indicator-dot ${r.status}`} title={`${r.subjectName}: ${r.status}`} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Date Detail Drawer Panel */}
        <div className="date-details-drawer">
          <div className="drawer-header">
            <h3>Day Details</h3>
            <span className="drawer-selected-date">
              {new Date(selectedDateStr).toLocaleDateString('default', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>

          <div className="drawer-body">
            {selectedDateDetails?.isWeekend ? (
              <div className="drawer-empty-state">
                <BeachAccessIcon style={{ fontSize: '48px', color: '#94a3b8' }} />
                <h4>Sunday Holiday</h4>
                <p>No standard academic lectures scheduled on weekends.</p>
              </div>
            ) : !selectedDateDetails?.records?.length ? (
              <div className="drawer-empty-state">
                <InfoIcon style={{ fontSize: '48px', color: '#cbd5e1' }} />
                <h4>No Roster Records</h4>
                <p>Attendance rosters are either empty or have not been finalized for this date yet.</p>
              </div>
            ) : (
              <div className="day-records-list">
                {(selectedDateDetails.records || []).map((record, index) => (
                  <div key={index} className="day-record-item-card">
                    <div className="record-item-meta">
                      <div className="record-title-wrap">
                        <span className="record-subject-name">{record.subjectName}</span>
                        <span className="record-period-text">{record.period}</span>
                      </div>
                      <span className={`record-status-pill ${record.status}`}>
                        {record.status}
                      </span>
                    </div>

                    {record.remark && (
                      <div className="record-remark-block">
                        <strong>Remark:</strong> {record.remark}
                      </div>
                    )}

                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default StudentAttendanceView;
